import React from 'react';
import { TrendingUp, AlertTriangle, CheckCircle, Calendar } from 'lucide-react';
import { DVC_RESORTS } from '../data/dvcData';
import { format, addMonths } from 'date-fns';

const PointsAnalysis = ({ contracts, vacations, pointsAnalysis }) => {
  if (!pointsAnalysis || contracts.length === 0) {
    return (
      <div className="card">
        <h2>Points Analysis</h2>
        <div className="alert alert-info">
          <p>Add contracts and plan vacations to see your points analysis.</p>
        </div>
      </div>
    );
  }

  const { vacationResults, contractSummary } = pointsAnalysis;
  
  const getTotalShortfall = () => {
    return vacationResults.reduce((total, result) => total + result.shortfall, 0);
  };

  const getBookingTimeline = () => {
    const timeline = [];
    
    vacationResults.forEach(vacResult => {
      // Only show booking windows for contracts that are actually allocated to this vacation
      if (vacResult.allocations && vacResult.allocations.length > 0) {
        vacResult.vacation.stays.forEach(stay => {
          vacResult.allocations.forEach(allocation => {
            const checkInDate = new Date(stay.checkIn);
            const isHomeResort = allocation.homeResort === stay.resort;
            const monthsInAdvance = isHomeResort ? 11 : 7;
            const bookingOpenDate = addMonths(checkInDate, -monthsInAdvance);
            
            timeline.push({
              date: bookingOpenDate,
              vacation: vacResult.vacation.name,
              resort: stay.resort,
              contract: allocation.homeResort,
              isHomeResort,
              canBookNow: new Date() >= bookingOpenDate,
              pointsAllocated: allocation.totalPoints || allocation.points || 0
            });
          });
        });
      }
    });
    
    return timeline
      .filter((item, index, self) => 
        index === self.findIndex(t => 
          t.date.getTime() === item.date.getTime() && 
          t.resort === item.resort &&
          t.contract === item.contract
        )
      )
      .sort((a, b) => a.date - b.date)
      .slice(0, 10); // Show next 10 booking opportunities
  };

  return (
    <div className="card">
      <h2>Points Analysis</h2>
      
      {/* Overall Status */}
      <div className="grid grid-3" style={{ marginBottom: '24px' }}>
        <div className="card" style={{ margin: 0, textAlign: 'center', border: '1px solid #ddd' }}>
          <div className="points-display" style={{ color: getTotalShortfall() > 0 ? '#dc3545' : '#28a745' }}>
            {getTotalShortfall()}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>Points Short</div>
        </div>
        
        <div className="card" style={{ margin: 0, textAlign: 'center', border: '1px solid #ddd' }}>
          <div className="points-display">
            {vacationResults.filter(vr => vr.isFullyFunded).length}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>Funded Vacations</div>
        </div>
        
        <div className="card" style={{ margin: 0, textAlign: 'center', border: '1px solid #ddd' }}>
          <div className="points-display">
            {Object.values(contractSummary).reduce((total, cs) => total + cs.available, 0)}
          </div>
          <div style={{ fontSize: '14px', color: '#666' }}>Max Spendable</div>
        </div>
      </div>

      {/* Contract Summary */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
          <TrendingUp size={20} style={{ marginRight: '8px' }} />
          Contract Utilization
        </h3>
        
        <div className="grid grid-2">
          {Object.values(contractSummary).map(cs => {
            const contract = cs.contract;
            const currentUseYear = contract.getCurrentUseYear();
            
            // Use the remaining points from contractSummary (after allocations)
            const remainingBreakdown = {
              banked: cs.bankedRemaining || 0,
              current: cs.currentRemaining || 0,
              borrowable: cs.borrowableRemaining || 0,
              total: cs.available || 0
            };
            
            // Calculate utilization based on allocated vs total contract points
            // Total contract points = allocated + remaining
            const totalContractPoints = cs.allocated + remainingBreakdown.total;
            const utilizationRate = totalContractPoints > 0 ? (cs.allocated / totalContractPoints) * 100 : 0;
            
            // Debug logging to see what's happening
            if (process.env.NODE_ENV === 'development') {
              console.log(`${contract.homeResort}: allocated=${cs.allocated}, remaining=${remainingBreakdown.total}, total=${totalContractPoints}, rate=${Math.round(utilizationRate)}%`);
            }
            
            return (
              <div key={contract.id} className="card" style={{ margin: 0, border: '1px solid #ddd' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0 }}>
                    {DVC_RESORTS[contract.homeResort]?.name.split(' ')[0] || contract.homeResort}
                  </h4>
                  <span className={`badge ${utilizationRate > 80 ? 'badge-warning' : utilizationRate > 50 ? 'badge-success' : 'badge-success'}`}>
                    {Math.round(utilizationRate)}% used
                  </span>
                </div>
                
                {/* Allocated Points */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px' }}>
                  <span>Allocated to Vacations:</span>
                  <strong style={{ color: '#dc3545' }}>{cs.allocated} pts</strong>
                </div>

                {/* Use Year Breakdown */}
                <div style={{ marginBottom: '12px', padding: '8px', background: '#f8f9fa', borderRadius: '4px' }}>
                  {/* Headers */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px', color: '#666', borderBottom: '1px solid #dee2e6', paddingBottom: '4px' }}>
                    <span>Use Year</span>
                    <span>Points Remaining</span>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '2px' }}>
                    <span>{format(new Date(currentUseYear - 1, contract.useYearMonth - 1), 'MMMM yyyy')}</span>
                    <strong style={{ color: '#28a745' }}>{remainingBreakdown.banked} pts</strong>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '2px' }}>
                    <span>{format(new Date(currentUseYear, contract.useYearMonth - 1), 'MMMM yyyy')}</span>
                    <strong style={{ color: '#007bff' }}>{remainingBreakdown.current} pts</strong>
                  </div>
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                    <span>{format(new Date(currentUseYear + 1, contract.useYearMonth - 1), 'MMMM yyyy')}</span>
                    <strong style={{ color: '#ffc107' }}>{remainingBreakdown.borrowable} pts</strong>
                  </div>
                  
                  <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #dee2e6' }} />
                  
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 'bold' }}>
                    <span>Remaining Total:</span>
                    <strong style={{ color: '#dc3545' }}>{remainingBreakdown.total} pts</strong>
                  </div>
                </div>
                
                {/* Progress bar */}
                <div style={{ 
                  width: '100%', 
                  height: '6px', 
                  background: '#e9ecef', 
                  borderRadius: '3px', 
                  marginTop: '12px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${Math.min(100, utilizationRate)}%`,
                    height: '100%',
                    background: utilizationRate > 80 ? '#ffc107' : '#28a745',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Vacation Funding Status */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
          <CheckCircle size={20} style={{ marginRight: '8px' }} />
          Vacation Funding Status
        </h3>
        
        {vacationResults.map(vacResult => (
          <div key={vacResult.vacation.id} className={`alert ${vacResult.isFullyFunded ? 'alert-success' : 'alert-warning'}`} style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>{vacResult.vacation.name}</strong>
                <div style={{ fontSize: '14px', marginTop: '4px' }}>
                  {vacResult.pointsAllocated} / {vacResult.pointsNeeded} points allocated
                  {vacResult.shortfall > 0 && (
                    <span style={{ color: '#dc3545', marginLeft: '8px' }}>
                      ({vacResult.shortfall} short)
                    </span>
                  )}
                </div>
              </div>
              
              {vacResult.isFullyFunded ? (
                <CheckCircle size={20} style={{ color: '#28a745' }} />
              ) : (
                <AlertTriangle size={20} style={{ color: '#ffc107' }} />
              )}
            </div>
            
            {vacResult.allocations.length > 0 && (
              <div style={{ marginTop: '8px', fontSize: '12px' }}>
                <strong>Point Sources:</strong>
                {vacResult.allocations.map((alloc, idx) => {
                  const resortName = DVC_RESORTS[alloc.homeResort]?.name.split(' ')[0] || alloc.homeResort;
                  const breakdown = [];
                  if (alloc.banked > 0) breakdown.push(`${alloc.banked}B`);
                  if (alloc.current > 0) breakdown.push(`${alloc.current}C`);
                  if (alloc.borrowed > 0) breakdown.push(`${alloc.borrowed}Br`);
                  
                  return (
                    <span key={idx} style={{ marginLeft: '8px' }}>
                      {resortName}: {alloc.totalPoints}pts ({breakdown.join('+')})
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Booking Timeline */}
      <div>
        <h3 style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
          <Calendar size={20} style={{ marginRight: '8px' }} />
          Upcoming Booking Windows
        </h3>
        
        {getBookingTimeline().length === 0 ? (
          <div className="alert alert-info">
            <p>No upcoming booking windows. Plan some vacations to see when you can book!</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Vacation</th>
                  <th>Resort</th>
                  <th>Contract</th>
                  <th>Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {getBookingTimeline().map((item, idx) => (
                  <tr key={idx}>
                    <td>{format(item.date, 'MMM dd, yyyy')}</td>
                    <td>{item.vacation}</td>
                    <td>{DVC_RESORTS[item.resort]?.name.split(' ')[0] || item.resort}</td>
                    <td>{DVC_RESORTS[item.contract]?.name.split(' ')[0] || item.contract}</td>
                    <td>
                      <span className={`badge ${item.isHomeResort ? 'badge-success' : 'badge-warning'}`}>
                        {item.isHomeResort ? 'Home (11mo)' : 'Other (7mo)'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${item.canBookNow ? 'badge-success' : 'badge-warning'}`}>
                        {item.canBookNow ? 'Book Now' : 'Waiting'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {getTotalShortfall() > 0 && (
        <div className="alert alert-warning" style={{ marginTop: '24px' }}>
          <h4 style={{ margin: '0 0 8px 0' }}>⚠️ Action Required</h4>
          <p>You're short {getTotalShortfall()} points for your planned vacations. Consider:</p>
          <ul style={{ margin: '8px 0 0 20px' }}>
            <li>Banking unused points from previous years</li>
            <li>Borrowing points from next year (up to 50% of annual allocation)</li>
            <li>Purchasing additional points</li>
            <li>Modifying vacation plans to require fewer points</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default PointsAnalysis;
